import { v4 } from 'uuid';
import { addEventListener, isFunction, base64Encode, isUrlInIgnoreList } from '@fpkg/stats-utils';
import { ErrorInfo, EventHandler, FilterEventFn } from '@fpkg/stats-core';

export const getCookie = (name: string): string => {
  if (!document.cookie || document.cookie.length <= 0) return '';
  const cookieArr = document.cookie.split(';');
  for (let i = 0; i < cookieArr.length; i++) {
    if (cookieArr[i] && cookieArr[i].length > 0) {
      const arr = cookieArr[i].split('=');
      if (arr[0] && arr[0].replace(/(^\s*)|(\s*$)/g, '') === name) {
        return decodeURIComponent(arr[1]);
      }
    }
  }
  return '';
};

/**
 * CookieUtils 工具类
 * @param cname key
 * @param cvalue value
 * @param exdays expired time , default half an hour
 */
export const setCookie = (cname: string, cvalue: string | number = '', exdays: number = 1 / 48): void => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  // add path in case of different env gets error,add for loginToken 599900
  document.cookie = `${cname}=${encodeURIComponent(cvalue)};expires=${d.toUTCString()};path=/`;
};

export type SinglePageEventCallback = (current_url: string) => void;

/**
 * 添加单页面事件监听
 * @param callback(last_url) 切面切换时回调，last_url上一个页面链接
 */
export const addSinglePageEvent = (callback: SinglePageEventCallback) => {
  let current_url = window.location.href;
  const historyPushState = window.history.pushState;
  const historyReplaceState = window.history.replaceState;

  if (isFunction(window.history.pushState)) {
    window.history.pushState = function pushState(...args) {
      historyPushState.apply(window.history, args);
      callback(current_url);
      current_url = window.location.href;
    };
  }

  if (isFunction(window.history.replaceState)) {
    window.history.replaceState = function replaceState(...args) {
      historyReplaceState.apply(window.history, args);
      callback(current_url);
      current_url = window.location.href;
    };
  }

  let singlePageEvent;

  if ((document as any).documentMode) {
    singlePageEvent = 'hashchange';
  } else {
    singlePageEvent = 'popstate';
  }

  addEventListener(singlePageEvent, () => {
    callback(current_url);
    current_url = window.location.href;
  });
};

/**
 * 监听document.load事件
 * @param callback
 */
export const onLoad = (callback: () => void) => {
  if (document.readyState === 'complete') {
    setTimeout(callback, 0);
  } else {
    addEventListener('load', () => setTimeout(callback, 0));
  }
};

/**
 * 监听页面卸载事件
 * @param callback
 */
export function addWindowUnloadEvent(callback: () => void) {
  let flag = false;
  ['pagehide', 'beforeunload', 'unload'].forEach((type) => {
    addEventListener(
      type,
      () => {
        if (!flag) {
          flag = true;
          callback && callback();
        }
      },
      true
    );
  });
}

export const getFilterEventCallback =
  <T>(callback: EventHandler<T>, filterEvent?: FilterEventFn<T>): EventHandler<T> =>
  (event: T) => {
    if (filterEvent && typeof filterEvent === 'function') {
      filterEvent(event) && callback(event);
    } else {
      callback(event);
    }
  };

/**
 * 是否灰度环境
 * @returns {boolean}
 */
export const isInGray = () => {
  const { pathname = '' } = window.location || {};
  return /^\/g\//.test(pathname);
};

export const isProduction = () =>
  // 特别注意:ios不支持正则表达式的断言，如下代码会报错
  // return /(?<!\.sit)\.sf-express\.com$/gi.test(location.hostname);
  /^.*\.sf-express\.com$/gi.test(window.location.hostname) &&
  !/^.*\.sit\.sf-express\.com$/gi.test(window.location.hostname);

export const getURL = (urlStr?: string) => {
  let url = urlStr;
  if (typeof url !== 'string') {
    url = window.location.href;
  }
  try {
    url = decodeURI(url);
  } catch (err) {
    console.error(err);
  }
  return url;
};

// 生成sw8的traceId
export function generateTraceId(appCode: string, requestURL: string): string {
  const page = window.location.href;
  const traceIdStr = String(base64Encode(v4()));
  const segmentId = String(base64Encode(v4()));
  const index = 0;
  const service = String(base64Encode(appCode));
  const instance = String(base64Encode('web'));
  const endpoint = String(base64Encode(page));
  const peer = String(base64Encode(requestURL));
  return `${1}-${traceIdStr}-${segmentId}-${index}-${service}-${instance}-${endpoint}-${peer}`;
}

// 判断是否是跨域域名，协议、域名、端口都要相同
export function isSameOrigin(url: string, location: Location = window.location) {
  const { protocol, host } = location;
  return new RegExp(`^${protocol}//${host}(\\?|#|/|$)`).test(url);
}

/**
 * 获取响应header
 * @param xhr
 */
export const getResponseHeaderMap = (xhr: XMLHttpRequest): Record<string, string> => {
  if (!xhr || !('getAllResponseHeaders' in xhr)) return {};
  // Get the raw header string
  const headers = xhr.getAllResponseHeaders();
  const arr = headers.trim().split(/[\r\n]+/);
  const headerMap: Record<string, string> = {};
  arr.forEach((line) => {
    const parts = line.split(': ');
    const header = parts.shift();
    const value = parts.join(': ');
    headerMap[header!] = value;
  });
  return headerMap;
};

/**
 * 获取ax-开头的header对象，会将key的中划线转成下划线，ax-server-time转成server_time
 * @param xhr
 */
export const getResponseAxHeaderMap = (
  xhr: XMLHttpRequest,
  headerPrefix: string
): Record<string, string | number> | null => {
  const headerMap = getResponseHeaderMap(xhr);
  let resMap: Record<string, string | number> | null = null;

  try {
    Object.keys(headerMap).forEach((key) => {
      if (new RegExp(`^${headerPrefix}.+$`, 'g').test(key)) {
        !resMap && (resMap = {});
        // ax- 开头的header需要上报
        const key1 = key.replace(new RegExp(`^${headerPrefix}`, 'g'), '').replaceAll('-', '_');

        // 如果是数字字符串，转成number类型
        resMap[key1] = /^\d+$/.test(headerMap[key]) ? parseFloat(headerMap[key]) : headerMap[key];
      }
    });
  } catch (e) {
    console.error(e);
  }

  return resMap;
};

export function getBrowserInfo() {
  const ua = navigator.userAgent.toLowerCase();
  const browsers = [
    { name: 'IE', pattern: /msie ([\d.]+)/ },
    { name: 'IE', pattern: /rv:([\d.]+)\) like gecko/ },
    { name: 'EDGE', pattern: /edge\/([\d.]+)/ },
    { name: 'Firefox', pattern: /firefox\/([\d.]+)/ },
    { name: 'Opera', pattern: /(?:opera|opr).([\d.]+)/ },
    { name: 'Chrome', pattern: /chrome\/([\d.]+)/ },
    { name: 'Safari', pattern: /version\/([\d.]+).*safari/ },
  ];

  for (let index = 0; index < browsers.length; index++) {
    const browser = browsers[index];
    const match = ua.match(browser.pattern);
    if (match) {
      return {
        browserType: browser.name,
        browserVersion: match[1],
      };
    }
  }

  return {
    browserType: 'Unknown',
    browserVersion: 'Unknown',
  };
}

/**
 * 判断是否需要上报，当返回的数据success不为true时，默认排除一下情况
 * 1、未登录
 * 2、json类型请求
 * 3、APM和神策上报请求
 * @param data
 * @returns boolean
 */
export function isNeedReport(data: { url: string; status: string; response: any }): boolean {
  let response = data.response || {};
  try {
    response = typeof response === 'string' ? JSON.parse(response || '{}') : response;
  } catch (e) {
    response = {};
    console.warn('JSON.parse报错了', response, e);
  }

  const url = data.url || '';
  const status = data.status || 0;
  const isNoLogin =
    `${status}` === '401' ||
    `${status}` === '403' ||
    response.errorCode === 'NO_LOGIN' ||
    response.errorCode === 'NOT_LOGIN' ||
    response.errorCode === 'NO_AUTH' ||
    response.errorCode === 'NOT_AUTH' ||
    response.errorCode === '000001';
  const isIgnoreUrl =
    /^https:\/\/ubs\.sf-express\.com/i.test(url) ||
    /app-apm-apis\.sf-express\.com\/apmReport\/report\/frontend/i.test(url) ||
    /\.json$/i.test(url);
  return !response.success && !isNoLogin && !isIgnoreUrl;
}

const keyCache: Record<string, boolean> = {};
/**
 * 过滤错误信息
 * @param info ErrorInfo
 * @param {(string | RegExp)[]} ignoreErrors 与error的message匹配，忽略对应的JS错误。
 * @param {boolean} dedupe 当前发生的JS错误会和上一个错误比较，如果是同一个错误，是否不再上报。重复错误上报往往没有意义。
 *               为了避免循环上报，默认开启了去重。如果业务场景希望相同的错误能够重复上报，需要将此字段设置为false。
 */
export const checkErrorInfo = (info: ErrorInfo, ignoreErrors: (string | RegExp)[] = [], dedupe: boolean = true) => {
  // chrome 插件注入的JS异常不上报
  if (info.filename && info.filename.indexOf('chrome-extension://') === 0) {
    return false;
  }
  // 没有 message 不上报
  if (!info.message) {
    return false;
  }
  // 无意义的 message 不上报
  if (
    info.message === 'ResizeObserver loop limit exceeded' ||
    info.message === 'ResizeObserver loop completed with undelivered notifications.'
  ) {
    return false;
  }

  // 忽略配置的黑名单
  if (isUrlInIgnoreList(info.message, ignoreErrors)) {
    return false;
  }

  if (!dedupe) {
    return true;
  }

  // 去重判断
  if (keyCache[info.error_key!]) {
    return false;
  }
  keyCache[info.error_key!] = true;

  return true;
};

/* 函数防抖 */
export function debounce(fn: Function, interval: number) {
  let timer: any;
  const gapTime = interval || 1000; // 间隔时间，如果interval不传，则默认1000ms
  return function (...args: any) {
    clearTimeout(timer);
    // @ts-ignore
    const context = this;
    timer = setTimeout(() => {
      fn.apply(context, args);
    }, gapTime);
  };
}
